package com.eladfinish.bible.contest.data.common.stringResolver

import dev.icerock.moko.resources.desc.StringDesc
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.launch

/**
 * Resolves through the translations [WebStrings] preloaded before the app was mounted, so
 * [resolveString] is a synchronous lookup like on the other platforms.
 *
 * moko-resources' own `StringDesc.toLocalizedString()` is `suspend` on Kotlin/JS (it fetches the
 * translations over HTTP). Only if that preload failed does this fall back to it: an in-memory
 * cache, a background fetch on a miss, and "" for that one call.
 */
actual class StringResolver : IStringResolver {

    private val cache = mutableMapOf<StringDesc, String>()
    private val inFlight = mutableSetOf<StringDesc>()

    actual override fun resolveString(stringDesc: StringDesc): String {
        WebStrings.resolveOrNull(stringDesc)?.let { return it }
        cache[stringDesc]?.let { return it }

        if (inFlight.add(stringDesc)) {
            CoroutineScope(Dispatchers.Default).launch {
                try {
                    cache[stringDesc] = stringDesc.toLocalizedString()
                } finally {
                    inFlight.remove(stringDesc)
                }
            }
        }

        return ""
    }

    // Genuinely awaits the fetch instead of returning "" when the translations aren't loaded —
    // safe for any suspend call site that needs the real text (e.g. building a link map keyed by
    // this string, where an empty key can cause an infinite loop — see HyperlinkText.kt).
    actual suspend fun resolveStringSuspending(stringDesc: StringDesc): String {
        WebStrings.resolveOrNull(stringDesc)?.let { return it }
        cache[stringDesc]?.let { return it }
        return stringDesc.toLocalizedString().also { cache[stringDesc] = it }
    }
}
